import { memo, useContext } from 'react';
import { Handle, Position, useNodeConnections } from '@xyflow/react';
import { VariablesCtx } from '../EcaVariablesContext.jsx';

/**
 * Variable node — a minimal visual marker for a named ECA token (_var_<id>).
 *
 * Visual: colored dot + label, no card wrapper.
 * Multiple references (instances) can exist for the same varId.
 *
 * Handles:
 *   TOP  (target):  connect from a BOTTOM output of a source node → sets the token
 *   BOTTOM (source): connect to a TOP input of a target node → reads the token
 */
export const EcaVariableNode = memo(function EcaVariableNode({ id, data, selected }) {
  const { variables } = useContext(VariablesCtx);
  // Node-scoped subscription: re-renders only when THIS node's connections change.
  const connections = useNodeConnections({ id });

  const varMeta = variables[data.varId] ?? {};
  const color = varMeta.color ?? '#8B5CF6';
  const displayLabel = varMeta.label || data.varId.replace('_var_', '');

  const hasTopEdge    = connections.some(c => c.target === id && c.targetHandle === 'var-in');
  const hasBottomEdge = connections.some(c => c.source === id && c.sourceHandle === 'var-out');

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 6px',
        borderRadius: 999,
        background: selected ? `${color}1A` : `${color}0D`,
        boxShadow: selected
          ? `0 0 0 2px ${color}, 0 0 0 5px ${color}33`
          : `0 0 0 1.5px ${color}99`,
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'box-shadow 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {/* TOP handle — receives value from a source node's BOTTOM output */}
      <Handle
        type="target"
        position={Position.Top}
        id="var-in"
        style={{
          background: hasTopEdge ? color : '#fff',
          border: `2px solid ${color}`,
          width: 10,
          height: 10,
          top: -5,
        }}
      />

      {/* Colored dot */}
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
          boxShadow: `0 0 5px ${color}80`,
        }}
      />

      {/* Label */}
      <span
        style={{
          fontSize: 11,
          fontWeight: varMeta.label ? 600 : 400,
          color: varMeta.label ? '#374151' : '#9CA3AF',
          fontFamily: 'inherit',
        }}
      >
        {displayLabel}
      </span>

      {/* BOTTOM handle — provides value to a target node's TOP input */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="var-out"
        style={{
          background: hasBottomEdge ? color : '#fff',
          border: `2px solid ${color}`,
          width: 10,
          height: 10,
          bottom: -5,
        }}
      />
    </div>
  );
});
